import { takeLatest, put, call, putResolve } from 'redux-saga/effects';
import { BaseAction } from '@model/redux';
import { BookingApi, GetTourRoomsParams, PutTourRoomsParams } from '@model/iceberg/service/booking';
import { TourRoomsResponse } from '@model/iceberg/deal-finder/deal-finder';
import { TourReferencesActions } from '@state/tours/tour-references/tourReferencesOperations';
import { fetchBasket } from '@state/basket';
import { getErrorCode } from '@util/error';

export enum TourRoomsActions {
  FETCH_TOUR_ROOMS = '@TOURS/FETCH_TOUR_ROOMS',
  RECEIVE_TOUR_ROOMS_FAILURE = '@TOURS/RECEIVE_TOUR_ROOMS_FAILURE',
  SET_TOUR_ROOMS = '@TOURS/SET_TOUR_ROOMS',
  UNSET_TOUR_ROOMS = '@TOURS/UNSET_TOUR_ROOMS',
  PUT_TOUR_ROOMS = '@TOURS/PUT_TOUR_ROOMS',
  PUT_TOUR_ROOMS_FAILURE = '@TOURS/PUT_TOUR_ROOMS_FAILURE'
}

export interface TourRoomsState {
  hotels: Array<Array<TourRoomsResponse>> | null;
  loading: boolean;
  error: string | null;
}

export const INITIAL_TOUR_ROOMS_STATE: TourRoomsState = {
  loading: false,
  error: null,
  hotels: null
};

export interface FetchTourRoomsAction extends BaseAction {
  type: TourRoomsActions.FETCH_TOUR_ROOMS;
  payload: GetTourRoomsParams;
}

export const fetchTourRooms: (params: GetTourRoomsParams) => FetchTourRoomsAction = (params: GetTourRoomsParams) => ({
  type: TourRoomsActions.FETCH_TOUR_ROOMS,
  payload: params
});

export interface SetTourRoomsAction extends BaseAction {
  type: TourRoomsActions.SET_TOUR_ROOMS;
  payload: TourRoomsState;
}

export const setTourRooms: (params: TourRoomsState) => SetTourRoomsAction = (params: TourRoomsState) => ({
  type: TourRoomsActions.SET_TOUR_ROOMS,
  payload: params
});

export interface UnsetTourRoomsAction extends BaseAction {
  type: TourRoomsActions.UNSET_TOUR_ROOMS;
}

export const unsetTourRooms: () => UnsetTourRoomsAction = () => ({
  type: TourRoomsActions.UNSET_TOUR_ROOMS
});

export function* onFetchTourRooms() {
  yield takeLatest(TourRoomsActions.FETCH_TOUR_ROOMS, performFetchTourRooms);
}

export function callGetTourRooms(payload: GetTourRoomsParams) {
  const api = new BookingApi();
  return api
    .getTourRooms(payload)
    .then((response: any) => ({
      response
    }))
    .catch((error: any) => ({
      error
    }));
}

export function* performGetTourRooms(payload: GetTourRoomsParams) {
  const { response } = yield call(callGetTourRooms, payload);
  if (response) {
    if (response.data) {
      yield put(setTourRooms(response.data));
      return response.data;
    } else {
      yield put(receiveTourRoomsFailure(''));
      return null;
    }
  } else {
    yield put(receiveTourRoomsFailure(''));
    return null;
  }
}

export function* performFetchTourRooms({ payload }: any) {
  yield performGetTourRooms(payload);
}

export interface PutTourRoomsAction extends BaseAction {
  type: TourRoomsActions.PUT_TOUR_ROOMS;
  payload: PutTourRoomsParams;
}

export const putTourRooms: (params: PutTourRoomsParams) => PutTourRoomsAction = (params: PutTourRoomsParams) => ({
  type: TourRoomsActions.PUT_TOUR_ROOMS,
  payload: params
});

export function* onPutTourRooms() {
  yield takeLatest(TourRoomsActions.PUT_TOUR_ROOMS, performPutTourRooms);
}

export function* performPutTourRooms({ payload }: any) {
  const bookingApi: BookingApi = new BookingApi();
  const { searchToken } = payload;

  try {
    yield bookingApi.setTourRooms(payload);
    yield put(fetchTourRooms({ searchToken }));
    yield putResolve(fetchBasket(searchToken));
  } catch (e: any) {
    yield putResolve(putTourRoomsFailure(getErrorCode(e)));
  }
}

export const receiveTourRoomsFailure = (error: string) => ({
  type: TourRoomsActions.RECEIVE_TOUR_ROOMS_FAILURE,
  error
});

export const putTourRoomsFailure = (error: string) => ({
  type: TourRoomsActions.PUT_TOUR_ROOMS_FAILURE,
  error
});

export const tourRoomsReducer: any = (state: any = INITIAL_TOUR_ROOMS_STATE, { type, payload, error }: any) => {
  switch (type) {
    case TourRoomsActions.SET_TOUR_ROOMS:
      return { ...state, hotels: payload, loading: false, error: null };
    case TourRoomsActions.UNSET_TOUR_ROOMS:
    case TourReferencesActions.UNSET_TOUR_REFERENCES:
      return INITIAL_TOUR_ROOMS_STATE;
    case TourRoomsActions.FETCH_TOUR_ROOMS:
      return { ...state, loading: true, error: null };
    case TourRoomsActions.RECEIVE_TOUR_ROOMS_FAILURE:
      return { ...INITIAL_TOUR_ROOMS_STATE, error, loading: false };
    case TourRoomsActions.PUT_TOUR_ROOMS:
      return { ...state, loading: true };
    case TourRoomsActions.PUT_TOUR_ROOMS_FAILURE:
      return { ...state, error, loading: false };
    default:
      return state;
  }
};
